import { defineComponent as _defineComponent } from 'vue'
import InputWrapper from '@/components/vision/Inputs/InputWrapper.vue'
import { computed, ref } from 'vue'
import { onClickOutside } from '@vueuse/core'
import { useFloating, autoUpdate, flip, offset, size } from '@floating-ui/vue'
import VsText from '@/components/vision/VsText.vue'
import PickerListVirtualized from '@/components/vision/next/picker-list/PickerListVirtualized.vue'
import { SearchOptions, useFVSearchQuery } from '@/api/field-view'
import ChipFilter from '@/components/vision/next/ChipFilter.vue'
import { SearchResultDTO } from '@/models/fv-search-result-dto'
import { useRouter } from 'vue-router/composables'


export default /*#__PURE__*/_defineComponent({
  __name: 'FieldViewSearch',
  setup(__props) {

const router = useRouter()

const categories = [
  { id: 'locations', label: 'Locations' },
  { id: 'resources', label: 'Resources' },
  { id: 'jobs', label: 'Jobs' },
]
const selectedCategory = ref(categories[0])

const searchTerm = ref<string>()

const inputRef = ref<HTMLInputElement | null>(null)
const popoverRef = ref<HTMLElement | null>(null)
const containerRef = ref<HTMLElement | null>(null)
const searchRef = ref<HTMLElement | null>(null)

const expanded = ref<boolean>(false)

const { floatingStyles } = useFloating(containerRef, popoverRef, {
  strategy: 'fixed',
  open: expanded,
  middleware: [
    flip(),
    offset(4),
    size({
      apply({ rects, elements }) {
        Object.assign(elements.floating.style, {
          width: `${rects.reference.width}px`,
        })
      },
    }),
  ],
  whileElementsMounted(...args) {
    return autoUpdate(...args, { animationFrame: true })
  },
})

onClickOutside(searchRef, () => {
  expanded.value = false
})

const handleClickWrapper = () => {
  if (!expanded.value) {
    inputRef.value?.focus()
  }
}

const handleFocus = () => {
  expanded.value = true
}

const selectCategory = (item) => {
  selectedCategory.value = item
}

const handleFocusOut = (event: FocusEvent) => {
  // event.relatedTarget is the component that is gaining focus.
  // In Safari, onClick event on button won't focus the button, so event.relatedTarget = null
  // In other browsers, onClick event on button will focus it, so event.relatedTarget = button itself
  // https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button#clicking_and_focus
  if (
    event.relatedTarget &&
    !searchRef.value?.contains(event.relatedTarget as Node)
  ) {
    expanded.value = false
  }
}

const searchOptions = computed<SearchOptions>(() => {
  return {
    searchTerm: searchTerm.value,
    category: selectedCategory.value.id,
  } as any
})

const clear = () => {
  searchTerm.value = ''
}

const clickRow = ({ id, title, source }) => {
  expanded.value = false
  if (source === 'jobs') {
    searchTerm.value = title.split(' ')[0]?.replace('#', '')
    router.push({
      name: 'fvnext-ticket-info-panel-direct',
      params: { id },
    })
    return
  }
  searchTerm.value = title
  router.push({
    name: 'fvnext-info-panel',
    params: { id, source },
  })
}

const {
  data: searchResults,
  isFetched,
  isFetching,
  isInitialLoading,
  fetchNextPage,
} = useFVSearchQuery(searchOptions)

const items = computed(() => {
  return Object.freeze(
    searchResults.value?.pages
      .flatMap((p) => p.data)
      .map((s) => ({
        id: s.id,
        title: s.title,
        source: s.type,
        subTitle: buildSubTitleForRow(s),
      })) || []
  )
})
const showSkeleton = computed(() => {
  if (items.value.length === 0) {
    if (isInitialLoading.value || isFetching.value) {
      return true
    }
  }
})

const noResultsMessage = computed(() => {
  if (items.value.length === 0) {
    if (!(isInitialLoading.value || !isFetched.value)) {
      return 'No results found for "' + searchOptions.value.searchTerm + '"'
    }
  }
  return ''
})

const buildSubTitleForRow = (item: SearchResultDTO) => {
  if (item.type === 'locations') {
    return [
      item.details.locationType,
      item.details.parentName,
      item.details.ownerName,
    ]
      .filter(Boolean)
      .join(' · ')
  } else if (item.type === 'resources') {
    return [
      item.details.resourceType,
      item.details.status,
      item.details.ownerName,
    ]
      .filter(Boolean)
      .join(' · ')
  } else if (item.type === 'jobs') {
    return [
      item.details.divisionName,
      item.details.hubName,
      item.details.destinationLocation,
    ]
      .filter(Boolean)
      .join(' · ')
  }

  return ''
}

return { __sfc: true,router, categories, selectedCategory, searchTerm, inputRef, popoverRef, containerRef, searchRef, expanded, floatingStyles, handleClickWrapper, handleFocus, selectCategory, handleFocusOut, searchOptions, clear, clickRow, searchResults, isFetched, isFetching, isInitialLoading, fetchNextPage, items, showSkeleton, noResultsMessage, buildSubTitleForRow, InputWrapper, VsText, PickerListVirtualized, ChipFilter }
}

})